import {
    BaseChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/base-chart-config-helper';
import type { IChartRendererConfig } from '@tencent/chart/lib/chart-ui/interface';
import { CHART_TYPE } from '@tencent/chart/lib/chart-ui/interface';
import { STDLblPos } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import {
    DataLabel,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/data-label';
import { labelPositionTypeMap } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/map';
import type { ChartUIDataItem, ChartUILabel, ShapeStyle } from '../interface/chart-ui.interface';
import { ChartUILabelPosition, TextStyle } from '../interface/chart-ui.interface';
import { Coordinate } from './element/coordinate';
import type {
    IChartDataItem,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-item/data-item.interface';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import type {
    ChartLayoutInfo,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-component.interface';
import type {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import type { CoordinateOption } from '@tencent/chart/lib/antv/g2/interface';
import type {
    IPieChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/pie-chart.interface';
import type {
    IChartManualLayout,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';

export class PieChartConfigHelper extends BaseChartConfigHelper {
    public get type() {
        return CHART_TYPE.PIE_CHART;
    }

    public getRenderConfig(): IChartRendererConfig {
        const baseRenderConfig = super.getRenderConfig();

        return {
            ...baseRenderConfig,
            xAxis: false,
            yAxis: false,
        };
    }

    protected normalizeData() {
        super.normalizeData();

        this.data = this.data
            // 饼图在ui移除空值的点
            .filter(dataItem => !!dataItem.value);
    }

    protected normalizeDataItem(dataItem: IChartDataItem, dataItemIndex: number) {
        return {
            ...super.normalizeDataItem(dataItem, dataItemIndex),
            // 饼图里的负数需要取绝对值，g2才能正确识别到占比
            value: dataItem.value && Math.abs(dataItem.value),
        };
    }

    // 用于动态获取图形样式属性，应用于g2的geometry.style api
    // geometryType: 组合图中实际对应的geometry实例的key
    protected getGeometryStyle(chartDataItem: ChartUIDataItem): ShapeStyle {
        const { fill, border } = chartDataItem || {};

        return {
            fill,
            lineWidth: border?.lineWidth,
            lineDash: border?.lineDash,
            // chart库默认有个border，还没查出原因。先用这种方式显示兼容一下
            stroke: border?.stroke || fill,
            lineJoin: border?.lineJoin,
            customStyles: this.getGeometryCustomStyles(chartDataItem),
        };
    }

    protected getCoordinateConfig() {
        const coordinate = new PieChartCoordinate(this.subChart);
        return coordinate.getConfig();
    }

    protected getLimitInPlot() {
        // 饼图、还原图关闭，否则会对边框进行裁剪
        return false;
    }

    protected getDataLabel(label: IDataLabelProperty): DataLabel {
        return new PieDataLabel(label, this.context);
    }
}

export class PieChartCoordinate extends Coordinate {
    public static checkRadiusCollapsedByLabel(subChartProperty: IBaseSubChartProperty): boolean {
        const primaryLabelPosition = subChartProperty.series[0]?.dataLabels?.labelPosition;
        let isRadiusCollapsedByLabel = primaryLabelPosition === STDLblPos.outEnd
            || primaryLabelPosition === STDLblPos.bestFit;
        let hasLabelLayout = false;
        let hasLabelInSide = false;

        subChartProperty.data.forEach((item) => {
            const { label } = item;
            if (!label) return;

            if (label?.labelPosition === STDLblPos.inEnd || label?.labelPosition === STDLblPos.ctr) {
                hasLabelInSide = true;
            }

            if (label.layout) {
                hasLabelLayout = true;
            }
        });

        // 如果外层是out或者最佳匹配，但因为含有inEnd或者center影响变大的图，如果有layout，就会再变小
        if (isRadiusCollapsedByLabel && hasLabelInSide && !hasLabelLayout) {
            isRadiusCollapsedByLabel = false;
        }

        return isRadiusCollapsedByLabel;
    }

    public getConfig(): CoordinateOption {
        const coordinateConfig = super.getConfig();
        const { firstSliceAngle } = this.subChartProperty as IPieChartProperty;

        // 饼图只有一个series
        const isRadiusCollapsedByLabel = PieChartCoordinate.checkRadiusCollapsedByLabel(this.subChartProperty);
        coordinateConfig.cfg = {
            ...coordinateConfig.cfg,
            // label会挤压图表大小。在没有label的时候，图表大小接近1，其余接近0.867
            radius: isRadiusCollapsedByLabel ? 0.867 : 1,
        };

        if (!coordinateConfig.actions) {
            coordinateConfig.actions = [];
        }
        coordinateConfig.actions.push(['rotate', Math.PI * (firstSliceAngle / 180)]);

        return coordinateConfig;
    }
}

export class PieDataLabel extends DataLabel {
    public getConfig(): ChartUILabel {
        const labelConfig = super.getConfig();
        const { style, originPosition } = labelConfig;

        const labelStyle: TextStyle = {};

        if (
            originPosition === STDLblPos.ctr
        ) {
            labelStyle.textAlign = 'center';
        }

        return {
            ...labelConfig,
            autoRotate: false,
            style: {
                ...style,
                ...labelStyle,
            },
        };
    }

    protected getLabelPosition(
        value: number,
        dataPointLabel: IDataLabelProperty,
        layout?: IChartManualLayout,
    ): ChartUILabelPosition {
        // 有layout的时候，以OutEnd作为偏移基准点
        if (layout) {
            return ChartUILabelPosition.OutEnd;
        }

        if (dataPointLabel.labelPosition === STDLblPos.bestFit) {
            return ChartUILabelPosition.InEnd;
        }

        return labelPositionTypeMap[dataPointLabel.labelPosition || ''] || ChartUILabelPosition.OutEnd;
    }
}
